import { BfiApi, IApiResponse } from './bfiApi';
import { ISearchCriteria } from './common';
import { IAddress } from 'libs/store/src/cross';

export interface IEntityValidation {
    entityId: number;
    name: string;
    tin: string;
    stateIncorporated: string;
}

export interface ICompanyValidation {
    id: number;
    name: string;
}

export interface ICompany extends IAddress {
    id: number;
    name: string;
    noticesUserId: string;
    physicalAddressSameAsMailing: boolean;
    physicalAddressStreet1: string;
    physicalAddressStreet2: string;
    physicalAddressCity: string;
    physicalAddressState: string;
    physicalAddressZipCode: string;
    physicalAddressCounty?: string;
}

export interface IEntity extends IAddress {
    id: number;
    name: string;
    tin: string;
    entityType: number;
    stateIncorporated: string;
    yearIncorporated?: Date;
}

export interface IPrincipal extends IAddress {
    id: number;
    firstName: string;
    middleName: string;
    lastName: string;
    suffic: string;
    email: string;
    workPhone: string;
    mobilePhone: string;
    netWorth?: number | null;
    adjustedNetWorth?: number | null;
    userId: string;
    link?: IEntityPrincipalLink;
    userResponsibleId: string;
}

export interface IPrincipalValidate {
    id: number;
    email: string;
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
}

export interface IPrincipalTypeAhead extends ISearchCriteria {
    excludedEntityId?: number;
}

export interface IEntityTypeAhead extends ISearchCriteria {
    excludedPrincipalId?: number;
}

export interface IBorrowerSearchCriterai extends ISearchCriteria {
    isDeleted: boolean;
}

export interface IEntityPrincipalLink {
    entityId: number;
    principalId: number;
    percentOwnership?: number | null;
    isOfficer: boolean;
    title: string;
}

export interface IDeleteEntityContactRelationship {
    entityId: number;
    principalId: number;
}

export interface IDeleteBusinessContactRelationship {
    companyId: number;
    principalId: number;
}

export class BorrowerApi extends BfiApi {
    getHighlights(): Promise<IApiResponse> {
        return super.get('/borrowers/highlights');
    }

    getBorrowersGrid(criteria: IBorrowerSearchCriterai): Promise<IApiResponse> {
        return super.post('/borrowers/grid', criteria);
    }

    getCompaniesRecycleBin(criteria: IBorrowerSearchCriterai): Promise<IApiResponse> {
        return super.post('/borrowers/companies/recyclebin', criteria);
    }

    getEntitiesRecycleBin(criteria: IBorrowerSearchCriterai): Promise<IApiResponse> {
        return super.post('/borrowers/entities/recyclebin', criteria);
    }

    getPrincipalsRecycleBin(criteria: IBorrowerSearchCriterai): Promise<IApiResponse> {
        return super.post('/borrowers/principals/recyclebin', criteria);
    }

    validateCompany(model: ICompanyValidation): Promise<IApiResponse> {
        return super.post('/borrowers/companies/validate', model);
    }

    validateEntity(model: IEntityValidation): Promise<IApiResponse> {
        return super.post('/borrowers/entities/validate', model);
    }

    getCompanyById(id: number): Promise<IApiResponse> {
        return super.get(`/borrowers/companies/${id}`);
    }

    getEntityById(id: number): Promise<IApiResponse> {
        return super.get(`/borrowers/entities/${id}`);
    }

    saveCompany(model: ICompany): Promise<IApiResponse> {
        return super.post('/borrowers/companies', model);
    }

    recycleCompany(id: number, isDeleted: boolean): Promise<IApiResponse> {
        return super.post(`/borrowers/companies/${id}/recycle/${isDeleted}`);
    }

    deleteCompanyPerm(id: number): Promise<IApiResponse> {
        return super.post(`/borrowers/companies/${id}/delete/permanent`);
    }

    companiesTypeAhead(model: ISearchCriteria): Promise<IApiResponse> {
        return super.post('/borrowers/companies/typeahead', model);
    }

    getNoticeRecipientList(companyId: number): Promise<IApiResponse> {
        return super.get(`/borrowers/companies/${companyId}/noticeusersearch`);
    }

    companiesTypeAheadById(id: number): Promise<IApiResponse> {
        return super.get(`/borrowers/companies/${id}/typeahead`);
    }

    saveEntity(model: IEntity): Promise<IApiResponse> {
        return super.post('/borrowers/entities', model);
    }

    recycleEntity(id: number, isDeleted: boolean): Promise<IApiResponse> {
        return super.post(`/borrowers/entities/${id}/recycle/${isDeleted}`);
    }

    deleteEntityPerm(id: number): Promise<IApiResponse> {
        return super.post(`/borrowers/entities/${id}/delete/permanent`);
    }

    getPrincipalById(id: number): Promise<IApiResponse> {
        return super.get(`/borrowers/principals/${id}`);
    }

    getPrincipalSummaryById(id: number): Promise<IApiResponse> {
        return super.get(`/borrowers/principals/${id}/summary`);
    }

    validatePrincipal(model: IPrincipalValidate): Promise<IApiResponse> {
        return super.post('/borrowers/principals/validate', model);
    }

    savePrincipal(model: IPrincipal): Promise<IApiResponse> {
        return super.post('/borrowers/principals', model);
    }

    recyclePrincipal(id: number, isDeleted: boolean): Promise<IApiResponse> {
        return super.post(`/borrowers/principals/${id}/recycle/${isDeleted}`);
    }

    deletePrincipalPerm(id: number): Promise<IApiResponse> {
        return super.post(`/borrowers/principals/${id}/delete/permanent`);
    }

    principalsTypeAhead(model: IPrincipalTypeAhead): Promise<IApiResponse> {
        return super.post('/borrowers/principals/typeahead', model);
    }

    principalsTypeAheadById(id: number): Promise<IApiResponse> {
        return super.get(`/borrowers/principals/${id}/typeahead`);
    }

    entitiesTypeAhead(model: IEntityTypeAhead): Promise<IApiResponse> {
        return super.post('/borrowers/entities/typeahead', model);
    }

    entitiesTypeAheadById(id: number): Promise<IApiResponse> {
        return super.get(`/borrowers/entities/${id}/typeahead`);
    }

    entityPrincipalTypeAhead(model: IEntityTypeAhead): Promise<IApiResponse> {
        return super.post('/borrowers/entities/principals/typeahead', model);
    }

    entityPrincipalTypeAheadById(entityId: number, principalId: number): Promise<IApiResponse> {
        return super.get(`/borrowers/entities/${entityId}/principals/${principalId}typeahead`);
    }

    getEntityPrincipalLinks(entityId: number): Promise<IApiResponse> {
        return super.get(`/borrowers/entities/${entityId}/principals`);
    }

    getPrincipalEntityLinks(principalId: number): Promise<IApiResponse> {
        return super.get(`/borrowers/principals/${principalId}/entities`);
    }

    getEntityPrincipalLink(entityId: number, principalId: number): Promise<IApiResponse> {
        return super.get(`/borrowers/entities/${entityId}/principals/${principalId}`);
    }

    setEntityPrincipalLink(model: IEntityPrincipalLink): Promise<IApiResponse> {
        return super.post('/borrowers/entities/principals', model);
    }

    getEntityPrincipalLinkSummary(entityId: number, principalId: number): Promise<IApiResponse> {
        return super.get(`/borrowers/entities/${entityId}/principals/${principalId}/summary`);
    }

    deleteEntityPrincipalLink(model: IDeleteEntityContactRelationship): Promise<IApiResponse> {
        return super.post('/borrowers/entities/principals/delete', model);
    }

    getBusinessPrincipalLinkSummary(companyId: number, principalId: number): Promise<IApiResponse> {
        return super.get(`/borrowers/companies/${companyId}/principals/${principalId}/summary`);
    }

    deleteBusinessPrincipalLink(model: IDeleteBusinessContactRelationship): Promise<IApiResponse> {
        return super.post('/borrowers/companies/principals/delete', model);
    }

    getEntityAuditHistoryList(entityId: number): Promise<IApiResponse> {
        return super.get(`/borrowers/entities/${entityId}/auditing`);
    }

    getEntityAuditHistoryDetail(id: number): Promise<IApiResponse> {
        return super.get(`/borrowers/entities/auditing/${id}`);
    }
}
